/**
 * output-panel-stale-card: the ONE place that decides when a card re-mounts.
 *
 * A sandboxed card receives its payload once, at iframe load (`host:init`). If React
 * reuses the instance, the iframe keeps rendering the FIRST payload forever — with no
 * error, and with a stale picture that is indistinguishable from a correct one. The
 * only lever is the React key: change it and the iframe is rebuilt; keep it and the
 * card is frozen.
 *
 * This lived in THREE places before it lived here — `CardViewer.cardKey`,
 * `TextViewer.cardKey`, and the `__embedGen` stamp in `ChatInterface` — and each one
 * handled a different subset. The embed-refresh path was fixed and its comment even
 * recorded the symptom ("the 'had to reload the whole page' symptom"); the live
 * WebSocket path was left with a key of `index`, noted in a comment as "(unchanged)".
 * Cards emitted into a chat therefore never re-mounted at all. Four rounds of a live
 * bug report were spent below this line, on a payload that was correct the whole time.
 *
 * So: one function, both viewers, no per-path subsets.
 *
 * It applies to SANDBOXED kinds only, and that distinction matters (!750 review #1).
 * An inline Tier-1 card is an ordinary React component: it already receives new props
 * on a stable key and re-renders. Re-keying it on payload turns every update into an
 * unmount/remount, which throws away exactly what an in-process card exists to hold —
 * a half-typed Canvas input, an expanded section, scroll position, a running
 * animation. Three kinds are inline today (`robot.play`, `robot.canvas`,
 * `canvas_demo.board`), and `canvas_demo.board` is `mode: interactive`.
 */

import { cardIndexEntrySync } from './sandbox/cardIndex';

/**
 * djb2 over the serialized payload. Not a security hash — a cheap, stable answer to
 * "is this the same card as last time?".
 *
 * A content signature rather than a stamp, deliberately. A stamp has to be remembered
 * at every dispatch site, and the site that gets forgotten is exactly the one nobody
 * is thinking about — which is the history above. A signature needs no cooperation
 * from the sender and cannot be forgotten.
 */
// Memoised on payload IDENTITY (!750 review #2). `JSON.stringify` + a walk of the
// whole payload ran on every render of every card, and a card's payload can be large
// — a Canvas document, a multi-shape scene. Redux payloads are immutable values, so
// object identity is a sound cache key: a changed payload is a different object.
// A WeakMap holds no payload alive.
const _sigs = new WeakMap();

function signature(structured) {
  if (structured && typeof structured === 'object') {
    const hit = _sigs.get(structured);
    if (hit !== undefined) return hit;
    const computed = computeSignature(structured);
    _sigs.set(structured, computed);
    return computed;
  }
  return computeSignature(structured);
}

function computeSignature(structured) {
  let h = 5381;
  let s;
  try {
    s = JSON.stringify(structured);
  } catch (e) {
    return 'x';                 // cyclic/unserializable — fall back to the base alone
  }
  if (typeof s !== 'string') return 'x';
  for (let i = 0; i < s.length; i += 1) h = ((h * 33) ^ s.charCodeAt(i)) >>> 0;
  return h.toString(36);
}

/**
 * React key for a rendered card.
 *
 * `idx` keeps distinct cards in one list distinct (and makes paging between two cards
 * of the same generation re-mount). The signature makes a CHANGED payload a different
 * card, and — just as importantly — makes an UNCHANGED one the same card: a sandboxed
 * card must not be rebuilt on every parent render, or it restarts its animation and
 * drops whatever state it holds.
 *
 * `__embedGen` / `session` stay in the base so the embed-refresh and fresh-emit paths
 * keep their existing, tested behaviour; the signature is additive.
 */
/**
 * Whether a payload change should REBUILD this card rather than re-render it.
 *
 * True for a sandboxed kind, which reads its payload once at iframe load. False for
 * an inline one, which gets new props like any component and would lose its state.
 *
 * Also true when the index has not loaded yet, and that default is chosen rather than
 * fallen into: guessing "inline" would reintroduce a SILENT stale card, while guessing
 * "sandboxed" costs at most one avoidable remount in the brief window before the index
 * arrives. Prefer the visible, recoverable failure.
 */
function rebuildsOnChange(kind) {
  // Wrapped because this runs inside RENDER. A card index that is mocked, stubbed or
  // simply absent must degrade to the safe answer, not throw through the viewer and
  // blank the panel — which it did, in the existing CardViewer tests, the moment this
  // dependency was added.
  let entry;
  try {
    entry = typeof cardIndexEntrySync === 'function' ? cardIndexEntrySync(kind) : undefined;
  } catch (e) {
    entry = undefined;
  }
  if (entry === undefined) return true;      // index not loaded — see above
  if (entry === null) return false;          // no runtime card; GenericCard re-renders
  return !entry.renderer;                    // `renderer` marks an INLINE card
}

export function cardFreshnessKey(structured, idx) {
  const s = structured || {};
  const base = s.__embedGen != null
    ? `eg${s.__embedGen}-${idx}`
    : (s.session != null ? `sess-${s.session}-${idx}` : `${s.kind || 'card'}-${idx}`);
  return rebuildsOnChange(s.kind) ? `${base}-${signature(s)}` : base;
}

export default cardFreshnessKey;
